import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, MapPin, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-4" data-testid="contact-title">
            Contact <span className="gradient-text">Us</span>
          </h1>
          <p className="text-lg text-slate-300">
            Get in touch with the AGOC 2026 organizing committee
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="glass-card p-8 mb-8" data-testid="contact-info">
              <h2 className="text-2xl font-semibold mb-6">Get In Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="glass-card p-3 neon-glow">
                    <MapPin className="h-6 w-6 text-[#FF6F4A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Conference Venue</h3>
                    <p className="text-slate-300 text-sm">
                      Le Meridien<br />
                      (Old The Grand Bhagwati)<br />
                      Magdalla Circle, Dumas Road<br />
                      Surat, Gujarat 395007, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="glass-card p-3 neon-glow">
                    <Mail className="h-6 w-6 text-[#FFB6C1]" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a
                      href="mailto:sgaosurat2025@gmail.com"
                      className="text-[#FF6F4A] hover:text-[#FFB6C1] transition-colors text-sm"
                    >
                      sgaosurat2025@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="glass-card p-3 neon-glow">
                    <Phone className="h-6 w-6 text-[#FF6F4A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Registration Queries</h3>
                    <p className="text-slate-300 text-sm">Dr. Vrushal Bhansali: <a href="tel:+919173797939" className="text-[#FF6F4A] hover:text-[#FFB6C1]">9173797939</a></p>
                    <p className="text-slate-300 text-sm">Dr. Nikita Patel: <a href="tel:+919898587360" className="text-[#FF6F4A] hover:text-[#FFB6C1]">9898587360</a></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card overflow-hidden rounded-2xl" data-testid="map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.9774!2d72.7763!3d21.1536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f02e5eb9a3b%3A0x26d7e5c48fb3a8ea!2sThe%20Grand%20Bhagwati!5e0!3m2!1sen!2sin!4v1713443200000"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Le Meridien, Surat - Conference Venue"
              ></iframe>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="glass-card p-8">
              <h2 className="text-2xl font-semibold mb-6">Send Us a Message</h2>
              <div className="space-y-6">
                <div data-testid="form-field-name">
                  <Label htmlFor="name" className="text-white">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="glass-card border-white/20 text-white mt-2"
                    placeholder="Your name"
                  />
                </div>

                <div data-testid="form-field-email">
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="glass-card border-white/20 text-white mt-2"
                    placeholder="your@email.com"
                  />
                </div>

                <div data-testid="form-field-subject">
                  <Label htmlFor="subject" className="text-white">Subject *</Label>
                  <Input
                    id="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    className="glass-card border-white/20 text-white mt-2"
                    placeholder="Message subject"
                  />
                </div>

                <div data-testid="form-field-message">
                  <Label htmlFor="message" className="text-white">Message *</Label>
                  <Textarea
                    id="message"
                    required
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="glass-card border-white/20 text-white mt-2 min-h-[150px]"
                    placeholder="Your message..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold py-6 text-lg neon-glow"
                  data-testid="submit-contact-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;